/**
 * Anant Smart Flyer Services — Full flyer editor.
 * 20+ templates · A4/WA/Insta/FB/YouTube formats · html2canvas download · wa.me share
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';
import { Download, Share2, Save, Plus, Trash2, Grid3x3, Type, Image as ImageIcon, Sparkles } from 'lucide-react';

const TABS = [
  { key: 'templates', label: 'Templates' },
  { key: 'editor', label: 'Editor' },
  { key: 'my', label: 'My Flyers' },
  { key: 'marketplace', label: 'Marketplace' },
  { key: 'plan', label: 'Plan' },
];

const daysBetween = (a, b) => { try { return Math.max(0, Math.ceil((new Date(b) - a) / 86400000)); } catch { return 0; } };

export default function AnantSmartFlyer() {
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState('templates');
  const [editingFlyer, setEditingFlyer] = useState(null);
  const [flyerConfig, setFlyerConfig] = useState(defaultFlyerConfig());

  const load = async () => {
    try { const r = await api.get('/flyer/config'); setCfg(r.data); } catch { toast.error('Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  const startWithTemplate = (tpl) => {
    const fmt = cfg?.formats?.[0]?.code || 'wa_square';
    setEditingFlyer(null);
    setFlyerConfig({
      ...defaultFlyerConfig(),
      template_id: tpl.id, format_code: fmt,
      title: tpl.sample_title, subtitle: tpl.sample_sub, description: tpl.sample_desc,
      gradient: tpl.gradient, accent: tpl.accent, layout: tpl.layout,
    });
    setTab('editor');
  };

  const openFlyer = (fl) => {
    setEditingFlyer(fl);
    setFlyerConfig({ ...defaultFlyerConfig(), ...(fl.config || {}), template_id: fl.template_id, format_code: fl.format_code });
    setTab('editor');
  };

  if (!cfg) return <div className="min-h-screen bg-slate-950 text-emerald-300 flex items-center justify-center pt-20">Loading...</div>;

  const active = cfg.user?.is_active;
  const quotaLeft = cfg.user?.quota_remaining || 0;
  const expiry = cfg.user?.subscription?.expiry_at;
  const daysLeft = expiry ? daysBetween(new Date(), expiry) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-orange-950/40 to-slate-950 text-white pt-20 pb-20" data-testid="flyer-page">
      <div className="max-w-5xl mx-auto px-3">
        {/* Header */}
        <div className="rounded-2xl border-2 border-orange-400/40 bg-gradient-to-r from-orange-700 via-amber-600 to-yellow-500 p-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="text-4xl">🎨</div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest font-black opacity-90">Anant Smart Flyer Services</div>
              <div className="text-lg font-black leading-tight">20+ Templates · Multi-Format · Unlimited Sharing</div>
              <div className="text-[10px] opacity-80 mt-0.5">300 AT/month · 3000 AT/year · 3 flyers per cycle · A4 / WA / Insta / FB / YouTube</div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            {active ? (
              <span className={`px-3 py-1 rounded-full text-xs font-black border-2 ${daysLeft <= 7 ? 'bg-amber-500/40 border-amber-300 animate-pulse' : 'bg-emerald-500/40 border-emerald-300'}`}
                    data-testid="flyer-plan-active">✅ ACTIVE · {daysLeft} days · {quotaLeft} flyers left</span>
            ) : (
              <span className="px-3 py-1 rounded-full text-xs font-black bg-red-500/40 border-2 border-red-300"
                    data-testid="flyer-plan-inactive">🔒 Not subscribed</span>
            )}
            <span className="px-3 py-1 rounded-full text-xs font-black bg-white/15 border-2 border-white/25">
              💰 {cfg.user?.at_balance?.toFixed(0) || 0} AT
            </span>
          </div>
        </div>

        <div className="flex gap-2 mb-4 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`flyer-tab-${t.key}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-black border-2 whitespace-nowrap ${tab === t.key ? 'bg-orange-500 border-orange-300 text-slate-900' : 'bg-slate-900 border-slate-700'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'templates' && <TemplatesGrid templates={cfg.templates} onPick={startWithTemplate} active={active} onAI={() => setTab('editor')} setFlyerConfig={setFlyerConfig} flyerConfig={flyerConfig} />}
        {tab === 'editor'    && <EditorTab cfg={cfg} flyerConfig={flyerConfig} setFlyerConfig={setFlyerConfig}
                                            editingFlyer={editingFlyer} onSaved={() => { load(); toast.success('✅ Flyer saved'); setTab('my'); }}
                                            active={active} quotaLeft={quotaLeft} />}
        {tab === 'my'        && <MyFlyersTab onEdit={openFlyer} onReload={load} />}
        {tab === 'marketplace' && <MarketplaceTab onReload={load} />}
        {tab === 'plan'      && <PlanTab cfg={cfg} onReload={load} />}
      </div>
    </div>
  );
}

function defaultFlyerConfig() {
  return {
    title: 'YOUR HEADLINE',
    subtitle: 'Amazing Offer',
    description: 'Grow your business today with our premium services',
    shop_name: 'Your Shop Name',
    mobile: '9999999999',
    whatsapp: '9999999999',
    website: 'anantwebportal.com',
    email: '',
    images: [],       // array of image URLs
    image_layout: 'single', // single | grid_2 | grid_4
    gradient: 'linear-gradient(135deg,#f59e0b,#dc2626)',
    accent: '#fef3c7',
    layout: 'hero_bottom',
    font_family: 'Poppins',
    text_color: '#ffffff',
    title_color: '#fbbf24',
  };
}

// ============ TEMPLATES GRID ============
function TemplatesGrid({ templates, onPick, active }) {
  const cats = useMemo(() => Array.from(new Set(templates.map((t) => t.category))), [templates]);
  const [cat, setCat] = useState(null);
  const filtered = cat ? templates.filter((t) => t.category === cat) : templates;
  return (
    <div className="space-y-3">
      {!active && <div className="p-3 rounded-xl bg-amber-900/40 border border-amber-500/50 text-xs">🔒 Active plan lene ke baad flyer save & download kar sakte hain.</div>}
      <div className="flex gap-2 overflow-x-auto pb-1">
        <button onClick={() => setCat(null)} className={`px-3 py-1.5 rounded-full text-xs font-black border-2 whitespace-nowrap ${!cat ? 'bg-orange-500 border-orange-300 text-slate-900' : 'bg-slate-900 border-slate-700'}`}>All</button>
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`flyer-cat-${c}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-black border-2 whitespace-nowrap ${cat === c ? 'bg-orange-500 border-orange-300 text-slate-900' : 'bg-slate-900 border-slate-700'}`}>
            {c}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {filtered.map((t) => (
          <button key={t.id} onClick={() => onPick(t)} data-testid={`flyer-tpl-${t.id}`}
                  className="rounded-xl border-2 border-slate-700 hover:border-orange-400 overflow-hidden transition transform hover:scale-105">
            <div className="aspect-[3/4] flex flex-col items-center justify-center text-center p-3 relative overflow-hidden" style={{ background: t.gradient }}>
              <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;utf8,%3Csvg%20xmlns=%22http://www.w3.org/2000/svg%22%20width=%2220%22%20height=%2220%22%3E%3Ccircle%20cx=%2210%22%20cy=%2210%22%20r=%221%22%20fill=%22white%22/%3E%3C/svg%3E')]" />
              <div className="text-[10px] uppercase tracking-widest font-black opacity-90 relative z-10" style={{ color: t.accent }}>{t.category}</div>
              <div className="text-sm font-black relative z-10 mt-1" style={{ color: t.accent }}>{t.sample_title}</div>
              <div className="text-[10px] opacity-80 relative z-10 mt-1 text-white">{t.sample_sub}</div>
            </div>
            <div className="p-2 bg-slate-900 text-xs font-black text-center">{t.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============ EDITOR ============
function EditorTab({ cfg, flyerConfig, setFlyerConfig, editingFlyer, onSaved, active, quotaLeft }) {
  const previewRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [imgUrl, setImgUrl] = useState('');
  const [aiIdea, setAiIdea] = useState('');
  const [aiBusiness, setAiBusiness] = useState('');
  const [aiTone, setAiTone] = useState('professional');
  const [aiBusy, setAiBusy] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [aiLastUsed, setAiLastUsed] = useState(false);
  const [showMktPublish, setShowMktPublish] = useState(false);
  const fmt = (cfg.formats || []).find((f) => f.code === flyerConfig.format_code) || cfg.formats[0];

  const update = (patch) => setFlyerConfig({ ...flyerConfig, ...patch });

  const aiGenerate = async (isRegen = false) => {
    if (!aiIdea || aiIdea.length < 5) { toast.error('Idea likhein (5+ chars)'); return; }
    if (!active) { toast.error('Active plan required'); return; }
    const confirmMsg = isRegen
      ? '🎲 Regenerate with different style — 20 AT lagega (mandatory). Continue?'
      : 'AI generation par 20 AT deduct hoga. Continue?';
    if (!window.confirm(confirmMsg)) return;
    setAiBusy(true);
    try {
      const r = await api.post('/flyer/ai-generate', { idea: aiIdea, business_type: aiBusiness, tone: aiTone });
      const g = r.data?.generated || {};
      update({
        title: g.title || flyerConfig.title,
        subtitle: g.subtitle || flyerConfig.subtitle,
        description: g.description || flyerConfig.description,
        gradient: g.gradient || flyerConfig.gradient,
        accent: g.accent || flyerConfig.accent,
        title_color: g.title_color || flyerConfig.title_color,
        text_color: g.text_color || flyerConfig.text_color,
        font_family: g.font_family || flyerConfig.font_family,
      });
      setAiLastUsed(true);
      toast.success(isRegen ? '🎲 New style generated! 20 AT charged.' : '🎨 AI generated! 20 AT charged.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'AI generation failed'); }
    finally { setAiBusy(false); }
  };

  const addImage = () => {
    const url = imgUrl.trim();
    if (!url) { toast.error('Paste image URL first'); return; }
    update({ images: [...(flyerConfig.images || []), url].slice(0, 10) });
    setImgUrl('');
  };
  const removeImage = (i) => {
    const next = [...(flyerConfig.images || [])];
    next.splice(i, 1);
    update({ images: next });
  };

  const downloadPNG = async () => {
    if (!previewRef.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(previewRef.current, { useCORS: true, allowTaint: true, backgroundColor: null, scale: 2 });
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url; a.download = `${(flyerConfig.title || 'flyer').replace(/[^a-z0-9]+/gi, '_')}.png`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      toast.success('📥 Downloaded');
      if (editingFlyer) api.post(`/flyer/${editingFlyer.id}/track?action=download`).catch(() => {});
    } catch (e) { toast.error('Download failed: ' + (e?.message || 'error')); }
    finally { setBusy(false); }
  };

  const shareWhatsApp = () => {
    const msg = `${flyerConfig.title}\n${flyerConfig.subtitle}\n\n${flyerConfig.description}\n\n📞 ${flyerConfig.mobile}\n🏬 ${flyerConfig.shop_name}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
    if (editingFlyer) api.post(`/flyer/${editingFlyer.id}/track?action=share`).catch(() => {});
  };

  const save = async () => {
    if (!active) { toast.error('Active plan required'); return; }
    if (!editingFlyer && quotaLeft <= 0) { toast.error('Quota exhausted — renew plan'); return; }
    setBusy(true);
    try {
      // capture thumbnail (auto-strip if too large so save never fails on payload size)
      let thumb = null;
      if (previewRef.current) {
        try {
          const canvas = await html2canvas(previewRef.current, { useCORS: true, allowTaint: true, scale: 0.4 });
          thumb = canvas.toDataURL('image/jpeg', 0.55);
          if (thumb && thumb.length > 550000) thumb = null;
        } catch { /* noop */ }
      }
      const res = await api.post('/flyer/save', {
        id: editingFlyer?.id || null,
        label: flyerConfig.title || 'Untitled',
        template_id: flyerConfig.template_id || 'custom',
        format_code: flyerConfig.format_code,
        config: flyerConfig,
        thumbnail_data_url: thumb,
      });
      if (res.data?.success) toast.success(editingFlyer ? '✅ Flyer updated' : '✅ Flyer saved to My Flyers');
      onSaved && onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Save failed — try again');
    }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {/* Preview canvas */}
      <div className="space-y-2">
        <div className="text-xs font-black opacity-70">Live Preview · {fmt?.label} ({fmt?.w}×{fmt?.h})</div>
        <div className="rounded-2xl overflow-hidden border-2 border-orange-400/40 bg-black">
          <div className="mx-auto" style={{ maxWidth: '400px', aspectRatio: `${fmt.w} / ${fmt.h}` }}>
            <FlyerCanvas ref={previewRef} config={flyerConfig} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button onClick={downloadPNG} disabled={busy} data-testid="flyer-download"
                  className="py-2 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black flex items-center justify-center gap-1 disabled:opacity-50">
            <Download size={12} /> PNG
          </button>
          <button onClick={shareWhatsApp} data-testid="flyer-share-wa"
                  className="py-2 rounded-lg bg-green-600 text-white text-xs font-black flex items-center justify-center gap-1">
            <Share2 size={12} /> WA
          </button>
          <button onClick={save} disabled={busy || !active} data-testid="flyer-save"
                  className="py-2 rounded-lg bg-orange-500 text-slate-900 text-xs font-black flex items-center justify-center gap-1 disabled:opacity-50">
            <Save size={12} /> {editingFlyer ? 'Update' : 'Save'}
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="space-y-2 p-3 rounded-2xl bg-slate-900 border border-slate-700">
        <button onClick={() => setShowAI(!showAI)} data-testid="flyer-ai-toggle"
                className="w-full py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 via-purple-500 to-cyan-500 text-white font-black text-xs flex items-center justify-center gap-1">
          <Sparkles size={12} /> {showAI ? '✕ Close AI' : 'AI Generate (20 AT)'}
        </button>
        {showAI && (
          <div className="p-3 rounded-lg bg-purple-950/50 border border-purple-500/40 space-y-2" data-testid="flyer-ai-panel">
            <input value={aiIdea} onChange={(e) => setAiIdea(e.target.value)}
                   placeholder="Rough idea (e.g. 'Diwali sale for saree shop 30% off')"
                   data-testid="flyer-ai-idea"
                   className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <div className="grid grid-cols-2 gap-2">
              <input value={aiBusiness} onChange={(e) => setAiBusiness(e.target.value)}
                     placeholder="Business type (saree, cafe...)"
                     className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <select value={aiTone} onChange={(e) => setAiTone(e.target.value)}
                      data-testid="flyer-ai-tone"
                      className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
                <option value="professional">Professional</option>
                <option value="festive">Festive</option>
                <option value="urgent">Urgent</option>
                <option value="friendly">Friendly</option>
                <option value="luxury">Luxury</option>
              </select>
            </div>
            <button onClick={() => aiGenerate(false)} disabled={aiBusy || !aiIdea} data-testid="flyer-ai-generate"
                    className="w-full py-2 rounded-lg bg-fuchsia-500 text-white text-xs font-black disabled:opacity-50">
              {aiBusy ? '🎨 Generating...' : aiLastUsed ? '✨ Generate Again (20 AT)' : '✨ Generate Flyer (20 AT)'}
            </button>
            {aiLastUsed && (
              <button onClick={() => aiGenerate(true)} disabled={aiBusy} data-testid="flyer-ai-regen"
                      className="w-full py-2 rounded-lg bg-gradient-to-r from-cyan-500 via-purple-500 to-fuchsia-500 text-white text-xs font-black disabled:opacity-50 animate-pulse">
                🎲 Try Another Style (20 AT)
              </button>
            )}
            <div className="text-[9px] text-amber-300">💰 Har AI call pe 20 AT deduct hoga (regen bhi). Fail hone pe automatic refund.</div>
          </div>
        )}
        <div className="text-xs font-black text-orange-300 mb-1 flex items-center gap-1"><Type size={12} /> Format & Fonts</div>
        <div className="grid grid-cols-2 gap-2">
          <select value={flyerConfig.format_code} onChange={(e) => update({ format_code: e.target.value })}
                  data-testid="flyer-format" className="px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs">
            {(cfg.formats || []).map((f) => <option key={f.code} value={f.code}>{f.label}</option>)}
          </select>
          <select value={flyerConfig.font_family} onChange={(e) => update({ font_family: e.target.value })}
                  data-testid="flyer-font" className="px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs">
            {(cfg.fonts || []).map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] opacity-70">Title Color</label>
            <input type="color" value={flyerConfig.title_color} onChange={(e) => update({ title_color: e.target.value })}
                   className="w-full h-8 rounded bg-slate-800 border border-slate-700" />
          </div>
          <div>
            <label className="text-[10px] opacity-70">Body Color</label>
            <input type="color" value={flyerConfig.text_color} onChange={(e) => update({ text_color: e.target.value })}
                   className="w-full h-8 rounded bg-slate-800 border border-slate-700" />
          </div>
        </div>
        <input value={flyerConfig.title} onChange={(e) => update({ title: e.target.value })} placeholder="Title"
               data-testid="flyer-title" className="w-full px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <input value={flyerConfig.subtitle} onChange={(e) => update({ subtitle: e.target.value })} placeholder="Subtitle"
               className="w-full px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <textarea value={flyerConfig.description} onChange={(e) => update({ description: e.target.value })} rows={2} placeholder="Description"
                  className="w-full px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />

        <div className="text-xs font-black text-orange-300 mt-2 flex items-center gap-1"><ImageIcon size={12} /> Images ({flyerConfig.images?.length || 0}/10)</div>
        <div className="flex gap-2">
          <input value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} placeholder="Paste image URL"
                 data-testid="flyer-img-url" className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <button onClick={addImage} data-testid="flyer-img-add" className="px-2 py-1.5 rounded bg-orange-500 text-slate-900 text-xs font-black"><Plus size={12} /></button>
        </div>
        <div className="flex gap-2 items-center">
          <label className="text-[10px] opacity-70">Layout:</label>
          {['single', 'grid_2', 'grid_4'].map((l) => (
            <button key={l} onClick={() => update({ image_layout: l })} data-testid={`flyer-layout-${l}`}
                    className={`px-2 py-1 rounded text-[10px] font-black ${flyerConfig.image_layout === l ? 'bg-orange-500 text-slate-900' : 'bg-slate-800'}`}>
              {l === 'single' ? '1×1' : l === 'grid_2' ? '2×1' : '2×2'}
            </button>
          ))}
        </div>
        {(flyerConfig.images || []).length > 0 && (
          <div className="grid grid-cols-4 gap-1">
            {flyerConfig.images.map((u, i) => (
              <div key={i} className="relative aspect-square">
                <img src={u} alt="" className="w-full h-full object-cover rounded" onError={(e) => { e.target.style.opacity = 0.3; }} />
                <button onClick={() => removeImage(i)} className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5"><Trash2 size={10} /></button>
              </div>
            ))}
          </div>
        )}

        <div className="text-xs font-black text-orange-300 mt-2">📞 Contact / Links</div>
        <div className="grid grid-cols-2 gap-2">
          <input value={flyerConfig.shop_name} onChange={(e) => update({ shop_name: e.target.value })} placeholder="Shop Name"
                 className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input value={flyerConfig.mobile} onChange={(e) => update({ mobile: e.target.value })} placeholder="Mobile"
                 className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input value={flyerConfig.whatsapp} onChange={(e) => update({ whatsapp: e.target.value })} placeholder="WhatsApp"
                 className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input value={flyerConfig.website} onChange={(e) => update({ website: e.target.value })} placeholder="Website"
                 className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>
      </div>
    </div>
  );
}

// ============ FLYER CANVAS (rendered flyer) ============
const FlyerCanvas = React.forwardRef(({ config }, ref) => {
  const imgs = config.images || [];
  const gridClass = config.image_layout === 'grid_4' ? 'grid-cols-2 grid-rows-2'
                  : config.image_layout === 'grid_2' ? 'grid-cols-2 grid-rows-1'
                  : 'grid-cols-1 grid-rows-1';
  return (
    <div ref={ref} className="w-full h-full relative flex flex-col overflow-hidden text-center p-4"
         style={{ background: config.gradient, fontFamily: config.font_family }}>
      {/* dotted pattern overlay */}
      <div className="absolute inset-0 opacity-20 pointer-events-none"
           style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '18px 18px' }} />
      <div className="relative z-10 flex flex-col h-full">
        <div className="text-[8px] uppercase tracking-widest font-black" style={{ color: config.accent }}>{config.shop_name}</div>
        <div className="text-2xl sm:text-3xl font-black leading-tight mt-2" style={{ color: config.title_color, textShadow: '0 2px 6px rgba(0,0,0,0.4)' }}>{config.title}</div>
        <div className="text-sm mt-1 font-bold" style={{ color: config.accent }}>{config.subtitle}</div>

        {imgs.length > 0 && (
          <div className={`grid ${gridClass} gap-1 flex-1 my-3 min-h-0`}>
            {imgs.slice(0, config.image_layout === 'single' ? 1 : config.image_layout === 'grid_2' ? 2 : 4).map((u, i) => (
              <img key={i} src={u} alt="" crossOrigin="anonymous"
                   className="w-full h-full object-cover rounded-lg border-2" style={{ borderColor: config.accent }}
                   onError={(e) => { e.target.style.display = 'none'; }} />
            ))}
          </div>
        )}
        {imgs.length === 0 && <div className="flex-1" />}

        <div className="text-xs px-2" style={{ color: config.text_color }}>{config.description}</div>
        <div className="mt-3 pt-2 border-t-2 text-[10px] space-y-0.5" style={{ borderColor: config.accent + '80', color: config.accent }}>
          {config.mobile && <div>📞 {config.mobile}{config.whatsapp && config.whatsapp !== config.mobile ? ` · 💬 ${config.whatsapp}` : ''}</div>}
          {config.website && <div>🌐 {config.website}</div>}
          {config.email && <div>✉️ {config.email}</div>}
        </div>
      </div>
    </div>
  );
});
FlyerCanvas.displayName = 'FlyerCanvas';

// ============ MY FLYERS ============
function MyFlyersTab({ onEdit, onReload }) {
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [mktModal, setMktModal] = useState(null);  // { flyer, label, description, category }
  const load = async () => {
    try { const r = await api.get('/flyer/list'); setRows(r.data?.flyers || []); } catch {}
  };
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm('Delete this flyer? Quota will NOT restore.')) return;
    await api.delete(`/flyer/${id}`); load(); onReload && onReload(); toast.success('Deleted');
  };
  const togglePublish = async (f) => {
    setBusyId(f.id);
    try {
      const r = await api.post(`/flyer/publish/${f.id}`, { make_public: !f.public });
      if (r.data.public) {
        const url = `${window.location.origin}/flyer/${r.data.slug}`;
        navigator.clipboard.writeText(url).then(() => toast.success('🔗 Public URL copied to clipboard'));
      } else {
        toast.success('Made private');
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusyId(null); }
  };
  const publishToMarketplace = async () => {
    if (!mktModal) return;
    try {
      await api.post('/flyer/marketplace/publish', {
        flyer_id: mktModal.flyer.id,
        marketplace_label: mktModal.label,
        marketplace_description: mktModal.description,
        category: mktModal.category || 'general',
      });
      toast.success('✅ Published to marketplace! Earn 10 AT per download.');
      setMktModal(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-2">
      {rows.length === 0 && <div className="text-xs opacity-60 text-center py-8">Koi flyer save nahin. Templates tab se start karein.</div>}
      {rows.map((f) => (
        <div key={f.id} className="p-3 rounded-xl bg-slate-900 border border-slate-800 flex items-center gap-3 flex-wrap" data-testid={`flyer-my-${f.id}`}>
          {f.thumbnail_data_url && <img src={f.thumbnail_data_url} alt="" className="w-14 h-14 object-cover rounded" />}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-black flex items-center gap-1">
              {f.label}
              {f.public && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/25 text-emerald-300 border border-emerald-400/40">🔗 Public</span>}
              {f.marketplace_source && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-500/25 text-purple-300">🛒 Bought</span>}
            </div>
            <div className="text-[10px] opacity-70">{f.format_code} · Shares: {f.share_count || 0} · Downloads: {f.download_count || 0}{f.public ? ` · Views: ${f.public_views || 0}` : ''}</div>
          </div>
          <div className="flex gap-1 flex-wrap">
            <button onClick={() => onEdit(f)} className="px-2 py-1 rounded bg-orange-500 text-slate-900 text-xs font-black">Edit</button>
            <button onClick={() => togglePublish(f)} disabled={busyId === f.id} data-testid={`flyer-publish-${f.id}`}
                    className={`px-2 py-1 rounded text-xs font-black ${f.public ? 'bg-cyan-600 text-white' : 'bg-slate-700 text-white'}`}>
              {f.public ? '🔗 Copy URL' : '🌐 Publish'}
            </button>
            {!f.marketplace_source && (
              <button onClick={() => setMktModal({ flyer: f, label: f.label, description: '', category: 'general' })}
                      data-testid={`flyer-mkt-${f.id}`}
                      className="px-2 py-1 rounded bg-purple-600 text-white text-xs font-black">🛒 Sell</button>
            )}
            <button onClick={() => del(f.id)} className="p-1.5 rounded bg-red-900/60"><Trash2 size={12} /></button>
          </div>
        </div>
      ))}
      {mktModal && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-3" onClick={() => setMktModal(null)}>
          <div className="bg-slate-900 border-2 border-purple-400 rounded-2xl w-full max-w-md p-4 text-white space-y-2" onClick={(e) => e.stopPropagation()}>
            <div className="text-lg font-black text-purple-300">🛒 Publish to Marketplace</div>
            <div className="text-xs opacity-70">Har download pe aap 10 AT earn karenge. Buyer 20 AT deta hai (10 aapko, 10 admin ko).</div>
            <input value={mktModal.label} onChange={(e) => setMktModal({ ...mktModal, label: e.target.value })} placeholder="Marketplace label"
                   className="w-full px-2 py-2 rounded bg-slate-800 border border-slate-700 text-sm" />
            <textarea value={mktModal.description} onChange={(e) => setMktModal({ ...mktModal, description: e.target.value })} rows={2}
                      placeholder="Description (why should someone buy this?)"
                      className="w-full px-2 py-2 rounded bg-slate-800 border border-slate-700 text-sm" />
            <select value={mktModal.category} onChange={(e) => setMktModal({ ...mktModal, category: e.target.value })}
                    className="w-full px-2 py-2 rounded bg-slate-800 border border-slate-700 text-sm">
              {['general', 'retail', 'fashion', 'food', 'service', 'business', 'events', 'realestate', 'education', 'automotive', 'finance', 'ecommerce'].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button onClick={publishToMarketplace} data-testid="flyer-mkt-submit"
                    className="w-full py-2 rounded bg-purple-500 text-white font-black">Publish · Earn 10 AT/Sale</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ MARKETPLACE (browse + buy) ============
function MarketplaceTab({ onReload }) {
  const [rows, setRows] = useState([]);
  const [category, setCategory] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const load = async () => {
    try {
      const r = await api.get('/flyer/marketplace' + (category ? `?category=${category}` : ''));
      setRows(r.data?.listings || []);
    } catch {}
  };
  useEffect(() => { load(); }, [category]);

  const buy = async (m) => {
    if (!window.confirm(`Purchase "${m.label}" for 20 AT?\n10 AT jaayega creator ko, 10 AT admin ko.`)) return;
    setBusyId(m.id);
    try {
      const r = await api.post(`/flyer/marketplace/${m.id}/purchase`);
      toast.success(r.data?.message || 'Purchased!');
      load(); onReload && onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Purchase failed'); }
    finally { setBusyId(null); }
  };

  const cats = ['general', 'retail', 'fashion', 'food', 'service', 'business', 'events'];

  return (
    <div className="space-y-3" data-testid="flyer-marketplace">
      <div className="p-3 rounded-2xl bg-gradient-to-r from-purple-700 to-fuchsia-700 text-center">
        <div className="text-xs uppercase tracking-widest opacity-90">🛒 Community Marketplace</div>
        <div className="text-lg font-black mt-1">Buy designs · 20 AT each</div>
        <div className="text-[10px] opacity-80">Creator 10 AT · Admin 10 AT · Zero free downloads</div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        <button onClick={() => setCategory(null)} className={`px-3 py-1 rounded-full text-xs font-black border-2 whitespace-nowrap ${!category ? 'bg-purple-500 border-purple-300 text-white' : 'bg-slate-900 border-slate-700'}`}>All</button>
        {cats.map((c) => (
          <button key={c} onClick={() => setCategory(c)} className={`px-3 py-1 rounded-full text-xs font-black border-2 whitespace-nowrap ${category === c ? 'bg-purple-500 border-purple-300 text-white' : 'bg-slate-900 border-slate-700'}`}>{c}</button>
        ))}
      </div>
      {rows.length === 0 && <div className="text-xs opacity-60 text-center py-8">No listings yet. Be the first to publish!</div>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {rows.map((m) => (
          <div key={m.id} className="rounded-xl border-2 border-slate-700 overflow-hidden bg-slate-900" data-testid={`flyer-mkt-item-${m.id}`}>
            {m.thumbnail_data_url && <img src={m.thumbnail_data_url} alt="" className="w-full aspect-[3/4] object-cover" />}
            <div className="p-2 space-y-1">
              <div className="text-xs font-black truncate">{m.label}</div>
              <div className="text-[9px] opacity-70">by @{m.creator_username} · {m.downloads || 0} sold</div>
              <button onClick={() => buy(m)} disabled={busyId === m.id} data-testid={`flyer-mkt-buy-${m.id}`}
                      className="w-full py-1.5 rounded bg-emerald-500 text-slate-900 text-xs font-black disabled:opacity-50">
                🛒 Buy · 20 AT
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ PLAN ============
function PlanTab({ cfg, onReload }) {
  const [selected, setSelected] = useState('monthly');
  const [busy, setBusy] = useState(false);
  const active = cfg.user?.is_active;
  const daysLeft = cfg.user?.subscription?.expiry_at ? daysBetween(new Date(), cfg.user.subscription.expiry_at) : 0;
  const canRenew = !active || daysLeft <= 7;
  const subscribe = async () => {
    if (!canRenew) { toast.error(`Plan already active — ${daysLeft} days left`); return; }
    setBusy(true);
    try {
      const r = await api.post('/flyer/subscribe', { plan: selected });
      toast.success(r.data?.message || 'Subscribed');
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {Object.values(cfg.plans || {}).map((p) => (
          <button key={p.code} onClick={() => setSelected(p.code)} data-testid={`flyer-plan-${p.code}`}
            className={`text-left p-4 rounded-2xl border-2 transition ${selected === p.code ? 'bg-orange-600/20 border-orange-400' : 'bg-slate-900 border-slate-700'}`}>
            <div className="text-xs uppercase font-black tracking-widest opacity-80">{p.label}</div>
            <div className="text-3xl font-black mt-1">{p.price_at} AT</div>
            <div className="text-xs opacity-70">{p.days} days · {p.quota} flyers</div>
            {p.code === 'yearly' && <div className="mt-2 text-[10px] px-2 py-1 rounded-full bg-amber-500/30 border border-amber-400/50 inline-block">🎁 Save 2 months</div>}
          </button>
        ))}
      </div>
      <button onClick={subscribe} disabled={busy || !canRenew} data-testid="flyer-subscribe-btn"
              className="w-full py-3 rounded-xl bg-orange-500 text-slate-900 font-black disabled:opacity-50">
        {busy ? 'Processing...' : active && !canRenew ? `🔒 Active (${daysLeft} days left)` : active ? '🔄 Renew Now' : '🚀 Activate Plan'}
      </button>
      <div className="p-3 rounded-xl bg-slate-900/60 border border-slate-800 text-xs opacity-80">
        <b className="text-orange-300">Key Features:</b>
        <ul className="mt-1 space-y-0.5 list-disc list-inside text-[11px]">
          <li>20+ professional templates</li>
          <li>7 formats (A4, WhatsApp, Instagram, Facebook, YouTube)</li>
          <li>Custom colors, fonts, images (up to 10 with grid layouts)</li>
          <li>One-click WhatsApp share + PNG download</li>
          <li>Unlimited sharing across all platforms</li>
        </ul>
      </div>
    </div>
  );
}
